import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  History,
  User,
  Files,
  Building,
  Clock,
} from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useMyApprovalHistory } from '@/hooks/approvals/useMyApprovalHistory';
import { format } from 'date-fns';

const MyApprovalHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: approvalHistory = [], isLoading } = useMyApprovalHistory();

  const formatCurrency = (amount: number, currency = 'ETB') => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const statusMeta = {
    approved: {
      label: 'Approved',
      variant: 'default' as const,
      icon: <CheckCircle className="h-5 w-5" />,
      chipIcon: <CheckCircle className="h-3 w-3" />,
      className: 'bg-green-100 text-green-600',
    },
    rejected: {
      label: 'Rejected',
      variant: 'destructive' as const,
      icon: <XCircle className="h-5 w-5" />,
      chipIcon: <XCircle className="h-3 w-3" />,
      className: 'bg-red-100 text-red-600',
    },
    mixed: {
      label: 'Mixed decisions',
      variant: 'outline' as const,
      icon: <Clock className="h-5 w-5" />,
      chipIcon: <Clock className="h-3 w-3" />,
      className: 'bg-muted text-muted-foreground',
    },
  };

  type ApprovalHistoryItem = (typeof approvalHistory)[number];

  type BulkHistoryGroup = {
    uploadId: string;
    items: ApprovalHistoryItem[];
    requestsCount: number;
    totalAmount: number;
    currency: string;
    departmentName: string | null;
    requesterName: string | null;
    latestApprovedAt: string;
    bulkStatus: 'approved' | 'rejected' | 'mixed';
    comments: string[];
  };

  type DisplayItem =
    | {
        type: 'single';
        item: ApprovalHistoryItem;
        sortDate: string;
      }
    | {
        type: 'bulk';
        group: BulkHistoryGroup;
        sortDate: string;
      };

  const displayItems = useMemo<DisplayItem[]>(() => {
    const bulkMap = new Map<string, ApprovalHistoryItem[]>();
    const singleItems: DisplayItem[] = [];

    approvalHistory.forEach((item) => {
      const uploadId = item.payment_request?.upload_id;
      const uploadMethod = item.payment_request?.upload_method;

      const isBulkChild = Boolean(uploadId && uploadMethod === 'bulk_csv');

      if (isBulkChild && uploadId) {
        const existing = bulkMap.get(uploadId) || [];
        existing.push(item);
        bulkMap.set(uploadId, existing);
        return;
      }

      singleItems.push({
        type: 'single',
        item,
        sortDate: item.approved_at,
      });
    });

    const bulkItems: DisplayItem[] = Array.from(bulkMap.entries()).map(
      ([uploadId, items]) => {
        const first = items[0];
        const statuses = Array.from(new Set(items.map((item) => item.status)));
        const bulkStatus =
          statuses.length === 1
            ? (statuses[0] as 'approved' | 'rejected')
            : 'mixed';

        const latestApprovedAt = items
          .map((item) => item.approved_at)
          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

        const totalAmount = items.reduce(
          (sum, item) => sum + (item.payment_request?.total_amount || 0),
          0
        );

        const requesterName = first.payment_request?.requester_first_name || first.payment_request?.requester_last_name
          ? `${first.payment_request?.requester_first_name || ''} ${first.payment_request?.requester_last_name || ''}`.trim()
          : null;

        return {
          type: 'bulk',
          group: {
            uploadId,
            items,
            requestsCount: items.length,
            totalAmount,
            currency: first.payment_request?.currency || 'ETB',
            departmentName: first.payment_request?.department?.name || null,
            requesterName,
            latestApprovedAt,
            bulkStatus,
            comments: items
              .map((item) => item.comments)
              .filter((comment): comment is string => Boolean(comment)),
          },
          sortDate: latestApprovedAt,
        };
      }
    );

    return [...bulkItems, ...singleItems].sort(
      (a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime()
    );
  }, [approvalHistory]);

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Approval History</h1>
          <p className="text-muted-foreground mt-1">
            Payment requests you have approved or rejected
          </p>
        </div>

        {displayItems.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No Approval History</h3>
              <p className="text-muted-foreground">
                You haven&apos;t approved or rejected any payment requests yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {displayItems.map((entry) => {
              if (entry.type === 'bulk') {
                const { group } = entry;
                const meta = statusMeta[group.bulkStatus];

                return (
                  <Card
                    key={`bulk-${group.uploadId}`}
                    className="hover:shadow-md transition-shadow cursor-pointer border-dashed"
                    onClick={() => navigate(`/my-approvals/bulk/${group.uploadId}`)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-full ${meta.className}`}>
                            {meta.icon}
                          </div>

                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="secondary" className="gap-1">
                                <Files className="h-3 w-3" />
                                Bulk request
                              </Badge>

                              <Badge variant={meta.variant}>
                                {meta.chipIcon}
                                <span className="ml-1">{meta.label}</span>
                              </Badge>
                            </div>

                            <h3 className="font-semibold">Uploaded from CSV</h3>

                            {group.requesterName && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {group.requesterName}
                              </p>
                            )}

                            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {format(
                                  new Date(group.latestApprovedAt),
                                  "MMM d, yyyy 'at' h:mm a"
                                )}
                              </span>

                              <span>{group.requestsCount} requests</span>

                              {group.departmentName && (
                                <span className="flex items-center gap-1">
                                  <Building className="h-3 w-3" />
                                  {group.departmentName}
                                </span>
                              )}
                            </div>

                            {group.comments.length > 0 && (
                              <p className="text-sm text-muted-foreground">
                                "{group.comments[0]}"
                                {group.comments.length > 1
                                  ? ` (+${group.comments.length - 1} more)`
                                  : ''}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="font-semibold text-lg">
                            {formatCurrency(group.totalAmount, group.currency)}
                          </p>
                          <p className="text-sm text-muted-foreground">Group total</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              const item = entry.item;
              const meta = statusMeta[item.status as 'approved' | 'rejected'];

              return (
                <Card
                  key={item.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/my-approvals/${item.payment_request_id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-full ${meta.className}`}>
                          {meta.icon}
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono">
                              {item.payment_request?.request_number}
                            </Badge>
                            <Badge variant={meta.variant}>{meta.label}</Badge>
                          </div>

                          <h3 className="font-semibold">{item.payment_request?.title}</h3>

                          {(item.payment_request?.requester_first_name ||
                            item.payment_request?.requester_last_name) && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {`${item.payment_request?.requester_first_name || ''} ${item.payment_request?.requester_last_name || ''}`.trim()}
                            </p>
                          )}

                          {item.comments && (
                            <p className="text-sm text-muted-foreground">
                              "{item.comments}"
                            </p>
                          )}

                          <p className="text-sm text-muted-foreground">
                            {format(new Date(item.approved_at), "MMM d, yyyy 'at' h:mm a")}
                          </p>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="font-semibold text-lg">
                          {formatCurrency(
                            item.payment_request?.total_amount || 0,
                            item.payment_request?.currency || 'ETB'
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {item.payment_request?.department?.name}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyApprovalHistoryPage;